import { APIStatus } from "@/api/APIStatus.type";
import { NewTag } from "@/components/Tag";
import {
  CompareToggleButton,
  CompareTogglePlayer,
  toCompareTogglePlayer,
  toStagedComparePlayer,
  useCompareToggle,
} from "@/components/organisms/Players/MrDbListCardView/PlayersCardView";
import { SearchBar } from "@/design-system/SearchBar/SearchBar";
import { Switch } from "@/design-system/Switch/Switch";
import { trackAddedPlayerToCompare } from "@/events/compareMixpanel";
import { cn } from "@/lib/utils";
import { addAlert } from "@/redux/message/messageSlice";
import { AppDispatch } from "@/redux/store";
import { useTranslation } from "@repo/shared/i18n";
import {
  addComparePlayer,
  comparePlayerKey,
  comparePlayersSelector,
  setCompareContext,
} from "@repo/shared/redux";
import { Chemistry, DetailedPlayer } from "@repo/shared/types";
import { useEffect, useMemo, useState } from "react";
import { Oval } from "react-loading-icons";
import { useDispatch, useSelector } from "react-redux";

import { playerSearchWithMetaRatings } from "../../../../../api/api";
import {
  squadBuilderFilterKeys,
  trackPlayerRecsChosen,
} from "../../../../../events/squadBuilderMixpanel";
import { useOutsideClick } from "../../../../../hooks/useOutsideClick";
import useSearch from "../../../../../hooks/useSearch";
import { PlayerRole } from "../../../../../redux/players/playersSlice";
import {
  activeFiltersSelector,
  defaultSquadBuilderSelector,
} from "../../../../../redux/squadBuilder/defaultSquadBuilder/defaultSquadBuilderSlice";
import { findMetaRating } from "../../../../../redux/squadBuilder/findMetaRating";
import {
  PlayerToInclude,
  SquadBuilderPlayerType,
  calculateMetaRatingAndChemistry,
  roleWeightsByRoleId,
} from "../../../../../redux/squadBuilder/squadBuilderUtils";
import { getUserSelector } from "../../../../../redux/user/userSlice";
import { SquadBuilderPlayer } from "../../../../molecules/Player/SquadBuilderPlayer/SquadBuilderPlayer";
import { SortByMetaRatingOrChemistry } from "../../../../molecules/SortByMetaRatingOrChemistry/SortByMetaRatingOrChemistry";
import { PanelContainer } from "../../components/PanelContainer/PanelContainer";
import { PanelHeader } from "../PanelHeader/PanelHeader";

// SquadBuilderPlayerType through the shared adapter, plus the chem context —
// that part is builder-specific, so it lives here.
const toSquadCompareTogglePlayer = (
  player: SquadBuilderPlayerType,
): CompareTogglePlayer => ({
  ...toCompareTogglePlayer(player),
  // Applied chem context of the pitch slot / recommendation — staged with the
  // player so the comparison opens at what the builder shows. Only pitch
  // players have a resolved chemstyle (0 = unresolved); recommendation and
  // search cards don't, but their shown meta rating assumes the best style at
  // the card's chemistry — resolve it the same way the pitch add does.
  chemistry: player.chemistry as Chemistry | undefined,
  // At 0 chem no style applies (always Basic) — only resolve above it.
  chemStyleId: !player.chemistry
    ? undefined
    : player.chemstyleId ||
      (player.metaRatings
        ? findMetaRating({
            metaRatings: player.metaRatings,
            playerRoleId: player.playerRoleId,
            chemistry: player.chemistry,
          })?.chemstyleId || undefined
        : undefined),
});

// Hook per card — needs its own component since the grid renders in a map.
// Always visible when rendered; the panel's toggle decides whether to render.
const CompareToggleOverlay = ({
  player,
  slotRoleId,
  badgeClassName,
}: {
  player: CompareTogglePlayer;
  /** The pitch slot's role — staged as compare context so the compare view
   * grades under it instead of "Best". */
  slotRoleId?: number;
  badgeClassName?: string;
}) => {
  const dispatch = useDispatch();

  const { isInCompare, handleToggleCompare } = useCompareToggle(player, {
    section: "Squad Builder",
    subsection: "Normal",
  });
  const handleToggle = (e: React.MouseEvent) => {
    if (!isInCompare && slotRoleId) {
      dispatch(setCompareContext({ playerRoleId: slotRoleId }));
    }
    handleToggleCompare(e);
  };
  return (
    <CompareToggleButton
      isInCompare={isInCompare}
      onToggle={handleToggle}
      badgeClassName={badgeClassName}
    />
  );
};

type Props = {
  handleClickedClose: () => void;
  handleDidAddPlayer: (
    playerRoleId: number,
    player: SquadBuilderPlayerType,
    temp?: boolean,
  ) => void;
  handleRemoveTempPlayer?: () => void;
  handleGetPlayerRecommendations: () => void;
  recommendedPlayers: SquadBuilderPlayerType[];
  playerRoles: PlayerRole[];
  playersToInclude?: PlayerToInclude[];
  changingPitchItemOnIndex?: number;
  availablePlayerRoles?: Record<string, boolean>;
  statusGetRecommendedPlayers?: APIStatus;
  playersOnPitch: SquadBuilderPlayerType[];
  playersOnBench: SquadBuilderPlayerType[];
  playerRoleId: number;
  tabletOrLargerMode?: boolean;
  desktopMode?: boolean;
  formationPosition?: string;
};

export const PlayerMode = ({
  handleClickedClose,
  handleDidAddPlayer,
  handleRemoveTempPlayer,
  handleGetPlayerRecommendations,
  recommendedPlayers,
  playersToInclude,
  changingPitchItemOnIndex,
  statusGetRecommendedPlayers,
  playerRoles,
  playersOnPitch,
  playersOnBench,
  playerRoleId,
  tabletOrLargerMode,
  formationPosition,
}: Props) => {
  const ref = useOutsideClick(() => {
    handleClickedClose();
  });
  const [sortBy, setSortBy] = useState<"meta-rating" | "chemistry">(
    "meta-rating",
  );
  const playerRoleName = useMemo(() => {
    return playerRoles.find((playerRole) => playerRole.id === playerRoleId)
      ?.name;
  }, [playerRoles, playerRoleId]);
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation("squadBuilder");
  const comparePlayers = useSelector(comparePlayersSelector);
  const { user } = useSelector(getUserSelector);
  const {
    scope,
    budget,
    squad,
    chosenTactic,
    formations,
    chemistryProfiles,
    clubLinks,
  } = useSelector(defaultSquadBuilderSelector);
  const activeFilters = useSelector(activeFiltersSelector);
  const [searchTerm, setSearchTerm, isSearching, result] = useSearch(
    "",
    (name: string, page: number) =>
      playerSearchWithMetaRatings(
        name,
        page,
        user.platform || "Playstation",
        playerRoleId ||
          playersToInclude[changingPitchItemOnIndex]?.playerRoleId,
      ),
  ) as [
    string,
    React.Dispatch<React.SetStateAction<string>>,
    boolean,
    SquadBuilderPlayerType[],
  ];
  const [players, setPlayers] = useState<SquadBuilderPlayerType[]>([]);
  const [noPlayersFound, setNoPlayersFound] = useState(false);
  const loading = statusGetRecommendedPlayers === "pending" || isSearching;

  // Compare overlay on the cards — starts on when a comparison is already
  // staged (matches the players-list behavior), toggleable from the panel.
  const [compareEnabled, setCompareEnabled] = useState(false);

  // The player currently occupying the slot this panel was opened for, if any.
  // Same index convention the recommendation cards already use to spot an
  // exchange (playersOnPitch[changingPitchItemOnIndex]).
  const playerOnSlot = playersOnPitch?.[changingPitchItemOnIndex];
  const slotRoleId =
    playerRoleId || playersToInclude?.[changingPitchItemOnIndex]?.playerRoleId;

  // Turning compare on stages the slot's current player, so the suggestions are
  // compared against the man they'd replace rather than an empty list.
  //
  // Turning it off is deliberately NOT the inverse: it only hides the card
  // overlays. Un-staging just the slot player would be arbitrary once the user
  // has added others by hand — on, +2 cards, off would drop the first and keep
  // the rest. Clearing the comparison is the widget's X, which owns that.
  //
  // A slot with nobody on it just toggles the overlays. Empty slots come
  // through as undefined (playersToInclude maps p.player), hence the
  // resourceId guard.
  const handleCompareToggle = (nextEnabled: boolean) => {
    setCompareEnabled(nextEnabled);
    if (!nextEnabled || !playerOnSlot?.resourceId) return;
    const staged = toStagedComparePlayer(
      toSquadCompareTogglePlayer(playerOnSlot),
    );
    // Same context the card overlay sets: grade the comparison under the slot's
    // role instead of each player's own best.
    if (slotRoleId) dispatch(setCompareContext({ playerRoleId: slotRoleId }));
    if (
      comparePlayers.some(
        (p) => comparePlayerKey(p) === comparePlayerKey(staged),
      )
    ) {
      return;
    }
    dispatch(
      addComparePlayer(staged as unknown as DetailedPlayer, {
        // 0 = the builder couldn't resolve a chemstyle — stage none.
        chemistry: playerOnSlot.chemistry as Chemistry | undefined,
        chemstyleId: playerOnSlot.chemstyleId || undefined,
      }),
    );
    trackAddedPlayerToCompare({
      origin: { section: "Squad Builder", subsection: "Normal" },
      source: "card",
      player_count: comparePlayers.length + 1,
    });
  };

  // Search results come without chemistry or team values, so work them out
  // with the player in this slot
  const withSlotChemistry = (
    player: SquadBuilderPlayerType,
  ): SquadBuilderPlayerType => {
    const formationId = chosenTactic.teamTactics.formationId;
    if (!formations[formationId] || !(changingPitchItemOnIndex <= 10))
      return player;
    const { chemistry, teamMetaRating } = calculateMetaRatingAndChemistry({
      players: Array.from({ length: 11 }, (_, i) =>
        i === changingPitchItemOnIndex ? player : playersOnPitch?.[i],
      ),
      manager: squad.manager,
      playerRoleIds: chosenTactic.playerRoles.map(
        (pr, i) =>
          (i === changingPitchItemOnIndex && slotRoleId) || pr.playerRoleId,
      ),
      formationId,
      chemistryProfiles,
      formations,
      clubLinks,
      roleWeights: roleWeightsByRoleId(playerRoles),
    });
    return {
      ...player,
      chemistry: chemistry.playersChemistry[changingPitchItemOnIndex] ?? 0,
      teamChemistry: chemistry.teamChemistry,
      teamMetaRating,
    };
  };

  useEffect(() => {
    let tempPlayers =
      result?.map(withSlotChemistry) || recommendedPlayers || [];
    tempPlayers = [...tempPlayers]?.sort((a, b) => {
      if (sortBy === "meta-rating") {
        return b.teamMetaRating - a.teamMetaRating;
      } else {
        if (a.teamChemistry !== b.teamChemistry) {
          return b.teamChemistry - a.teamChemistry;
        }
        return b.teamMetaRating - a.teamMetaRating;
      }
    });
    setPlayers(tempPlayers);
    setNoPlayersFound(
      tempPlayers.length === 0 && statusGetRecommendedPlayers === "fulfilled",
    );
  }, [result, recommendedPlayers, sortBy, playersToInclude]);

  useEffect(() => {
    if (changingPitchItemOnIndex >= 0) {
      handleGetPlayerRecommendations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changingPitchItemOnIndex]);

  return (
    <div ref={ref} className="flex flex-col">
      {/* <div ref={ref} className="flex flex-col"> */}
      <PanelHeader title={t("panel.addPlayer")} onClose={handleClickedClose} />
      <PanelContainer>
        <div className="flex flex-col w-full mx-auto mt-8 touch-device:mt-6">
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder={t("player.searchPlaceholder")}
            autoFocus={tabletOrLargerMode}
          />
        </div>
        <div className="flex flex-col w-full mt-2 gap-y-1 touch-device:mt-7">
          {(!isSearching || result?.length === 0) && (
            <>
              {/* <div className="text-dark-secondary ml-2 text-xs pt-4 pb-4 flex flex-row items-center gap-x-1">
                <span>Compare Players</span>
                <Switch
                  onChange={() => handleCompareToggle(!compareEnabled)}
                  isActive={compareEnabled}
                />
              </div> */}
              <p className="ml-3 text-xs text-foreground-muted">
                {t("player.aiSuggestionsFor", { role: playerRoleName })}
              </p>
              <SortByMetaRatingOrChemistry
                sortBy={sortBy}
                onChange={setSortBy}
              />
            </>
          )}
          {loading ? (
            <div className="mx-auto mt-8">
              <Oval width={36} height={36} />
            </div>
          ) : noPlayersFound && statusGetRecommendedPlayers === "fulfilled" ? (
            <p className="mx-auto mt-2 text-xs text-secondary px-4">
              {t("player.noneFound", {
                role: playerRoleName ?? t("player.fallbackRole"),
              })}
            </p>
          ) : (
            <div className="grid items-center justify-center grid-cols-2 mobile:mt-2 ml-4 mt-24 pb-16">
              {players.map((player) => {
                const mappedPlayerRole =
                  playerRoleId ||
                  playersToInclude[changingPitchItemOnIndex]?.playerRoleId ||
                  player.metaRatings[0]?.playerRoleId;

                /* 
                Checks whether the player is already on the pitch, and if so marks it as not allowed to be chosen
                unless it's on the same position as the current player on the pitch (i.e. exchanging player).
                */
                const playerAlreadyOnPitch =
                  playersOnPitch.some(
                    (p) => p.assetId && p.assetId === player.assetId,
                  ) &&
                  playersOnPitch[changingPitchItemOnIndex]?.assetId !==
                    player.assetId;

                const playerAlreadyOnBench = playersOnBench.some(
                  (p) => p.assetId && p.assetId === player.assetId,
                );

                return (
                  <div
                    key={
                      player.resourceId +
                      ("" + player?.eaPlayerId) +
                      ("" + player?.evoPathId)
                    }
                    className={cn(
                      "relative sb-v2-lg:mt-0 cursor-pointer",
                      playerAlreadyOnPitch &&
                        "brightness-30 cursor-not-allowed",
                      !tabletOrLargerMode
                        ? "scale-[190%] h-[210px] mt-1 w-[80px] left-1/4"
                        : "scale-[90%] sb-v2-lg:scale-[100%] -left-2",
                    )}
                    onMouseEnter={() => {
                      !playerAlreadyOnPitch &&
                        tabletOrLargerMode &&
                        handleDidAddPlayer(mappedPlayerRole, player, true);
                    }}
                    onMouseLeave={() =>
                      tabletOrLargerMode && handleRemoveTempPlayer()
                    }
                  >
                    <SquadBuilderPlayer
                      player={{
                        ...player,
                      }}
                      playerRoleId={mappedPlayerRole}
                      position={
                        player.possiblePositions.includes(formationPosition)
                          ? formationPosition
                          : player.possiblePositions.includes(player.position)
                            ? player.position
                            : player.preferredPosition
                      }
                      formationPosition={""}
                      chemStyleId={0}
                      {...(player.chemistry >= 0 && {
                        chemistry: {
                          currentChemistry: player.chemistry,
                          previousChemistry: player.chemistry,
                          isOutOfPosition: false,
                        },
                      })}
                      metaRating={
                        player.metaRatings.length === 0
                          ? 0
                          : player.metaRatings?.[player.chemistry ?? 0]
                              ?.metaRating
                      }
                      metaRatingZeroChem={player.metaRatings[0]?.metaRating}
                      tabletOrLargerMode={tabletOrLargerMode}
                      onClickPlayer={(e) => {
                        if (playerAlreadyOnBench) {
                          dispatch(
                            addAlert({
                              type: "warning",
                              title: t("player.movedToPitchTitle", {
                                name: player.cardName ?? t("unknownCard"),
                              }),
                              description: t("player.movedToPitch"),
                            }),
                          );
                        }
                        if (!playerAlreadyOnPitch) {
                          handleDidAddPlayer(mappedPlayerRole, player, false);
                          // Recommendations are shown when there's no active
                          // search result; only those are "recs chosen".
                          const isRecommendation =
                            !result || result.length === 0;
                          if (isRecommendation) {
                            trackPlayerRecsChosen({
                              scope,
                              filters: squadBuilderFilterKeys(
                                activeFilters,
                                scope,
                              ),
                              budget,
                              position: formationPosition,
                            });
                          }
                        }
                        e.stopPropagation();
                      }}
                    />
                    {compareEnabled && (
                      // Mobile only: this wrapper is taller than the card it
                      // holds (h-[210px] vs the card's own h-[110px]), so the
                      // toggle's inset-0 would centre ~50px below the card —
                      // and the 190% scale magnifies that to ~95px on screen.
                      // Re-box the overlay to the card's exact footprint
                      // (mirrors SquadBuilderPlayer's root) so it centres on
                      // the art, and counter-scale the badge out of the 190%.
                      // Desktop's wrapper shrink-wraps the card, so it needs
                      // neither and both classes stay off.
                      <div
                        className={cn(
                          !tabletOrLargerMode &&
                            "absolute top-0 left-0 w-20 h-[110px] mobile:h-[154px] mobile:w-28 large-desktop:w-32 large-desktop:h-[176px]",
                        )}
                      >
                        <CompareToggleOverlay
                          player={toSquadCompareTogglePlayer(player)}
                          // Slot-derived only — the metaRatings fallback in
                          // mappedPlayerRole is the player's own best role, not
                          // the spot being compared for.
                          slotRoleId={
                            playerRoleId ||
                            playersToInclude[changingPitchItemOnIndex]
                              ?.playerRoleId
                          }
                          badgeClassName={cn(
                            !tabletOrLargerMode && "scale-[55%]",
                          )}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </PanelContainer>
    </div>
  );
};
